import { useEffect, useState } from "react";
import axios from "axios";
import { Mail, FileText, BookOpen, Brain, Paperclip, Users, ExternalLink, Inbox } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { API } from "../App";
import { formatDateSafe } from "../lib/formatDate";
import { getModuleContent } from "../lib/psychoeducationExport";

const KIND = {
  worksheet: { icon: FileText, cls: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200", label: "Fiche" },
  module: { icon: BookOpen, cls: "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200", label: "Module" },
  analysis: { icon: Brain, cls: "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200", label: "Analyse" },
  file: { icon: Paperclip, cls: "bg-muted text-foreground", label: "Fichier" },
};

const attachmentLabel = (a) => {
  if (a.label) return a.label;
  if (a.kind === "module") {
    const m = a.name.replace(/^module-/, "").replace(/\.pdf$/i, "").match(/^(.*)-([a-z]{2})$/i);
    return m ? `${getModuleContent(m[1], m[2])?.title || getModuleContent(m[1], "fr")?.title || m[1]} (${m[2].toUpperCase()})` : a.name;
  }
  return a.name;
};

const Chip = ({ a, i }) => {
  const k = KIND[a.kind] || KIND.file;
  const Icon = k.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${k.cls}`} title={a.name} data-testid={`sent-attachment-${i}`}>
      <Icon className="w-3 h-3" /> {attachmentLabel(a)}
    </span>
  );
};

const TYPE_LABEL = { followup: "E-mail de suivi", analysis: "Analyse envoyée", parent_guidance: "Guidance parentale (parents)" };

const SentDocumentsPanel = ({ patientId, onOpenSession }) => {
  const [docs, setDocs] = useState(null);
  useEffect(() => {
    axios.get(`${API}/therapist/patients/${patientId}/sent-documents`, { withCredentials: true })
      .then((r) => setDocs(r.data.documents || [])).catch(() => setDocs([]));
  }, [patientId]);

  return (
    <Card data-testid="sent-documents-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><Mail className="w-5 h-5 text-primary" /> Documents envoyés par e-mail {docs && <span className="text-xs font-normal bg-primary/10 text-primary px-1.5 py-0.5 rounded-full" data-testid="sent-documents-count">{docs.length}</span>}</CardTitle>
        <p className="text-sm text-muted-foreground">Historique des e-mails de suivi, fiches, modules et analyses transmis à ce patient, du plus récent au plus ancien.</p>
      </CardHeader>
      <CardContent>
        {docs === null ? <p className="text-sm text-muted-foreground">Chargement…</p> : docs.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground" data-testid="sent-documents-empty">
            <Inbox className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Aucun document envoyé pour l'instant. Les envois depuis l'onglet Séances (e-mail de suivi, analyses) apparaîtront ici.</p>
          </div>
        ) : (
          <ol className="relative border-l border-border ml-2 space-y-5" data-testid="sent-documents-list">
            {docs.map((d, i) => (
              <li key={`${d.report_id}-${d.type}-${d.key || ""}-${d.date}`} className="ml-5" data-testid={`sent-document-${i}`}>
                <span className="absolute -left-[7px] mt-1.5 w-3.5 h-3.5 rounded-full bg-primary/80 ring-4 ring-background" />
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium flex items-center gap-2 flex-wrap">
                      {TYPE_LABEL[d.type] || d.type}{d.label && d.type !== "followup" ? ` — ${d.label}` : ""}
                      {d.couple && <span className="inline-flex items-center gap-1 text-[11px] text-rose-700 bg-rose-50 dark:bg-rose-950/30 px-1.5 py-0.5 rounded"><Users className="w-3 h-3" /> couple</span>}
                    </p>
                    <p className="text-xs text-muted-foreground" data-testid={`sent-document-meta-${i}`}>
                      {formatDateSafe(d.date, { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}{d.to ? ` · à ${d.to}` : ""}
                      {d.session_date ? ` · séance du ${formatDateSafe(d.session_date, { day: "numeric", month: "long", year: "numeric" })}` : ""}
                    </p>
                    {d.subject && <p className="text-xs mt-0.5 italic truncate max-w-xl" title={d.subject}>« {d.subject} »</p>}
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs shrink-0" onClick={() => onOpenSession?.(d.report_id)} data-testid={`sent-document-open-${i}`}>
                    <ExternalLink className="w-3.5 h-3.5" /> Voir la séance
                  </Button>
                </div>
                {d.type === "followup" && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {(d.attachments || []).length === 0 ? <span className="text-xs text-muted-foreground">Sans pièce jointe</span> : d.attachments.map((a, j) => <Chip key={j} a={a} i={`${i}-${j}`} />)}
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
};

export default SentDocumentsPanel;
